import React, { useState, useEffect, useRef } from "react";
import { useLanguage } from "contexts/LanguageContext";
import { FormattedMessage } from "react-intl";
import styles from "./LanguageSelector.module.css";
import { FaGlobe, FaChevronDown } from "react-icons/fa";

import BRFlag from "assets/images/br.png";
import USFlag from "assets/images/us.png";
import ESFlag from "assets/images/es.png";

const FLAG_SIZE = 24;
const FLAG_HEIGHT = 18;

const LanguageSelector = ({
  isMobile = false,
  isMobileMenuOpen = false,
  onLanguageChange = null
}) => {
  const { changeLanguage, locale } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false); // controle animação de fechamento
  const wrapperRef = useRef(null);

  const languages = [
    { code: "pt-br", name: "Português", flag: BRFlag },
    { code: "en-us", name: "English", flag: USFlag },
    { code: "es-es", name: "Español", flag: ESFlag }
  ];

  const selectedLanguage =
    languages.find(lang => lang.code === locale) || languages[0];

  const handleLanguageChange = langCode => {
    setIsClosing(true);
    setTimeout(() => {
      changeLanguage(langCode);
      setIsOpen(false);
      setIsClosing(false);

      // callback para notificar mudança de idioma
      if (onLanguageChange) {
        onLanguageChange(langCode);
      }
    }, 300);
  };

  // fecha o dropdown ao clicar fora (apenas em desktop)
  useEffect(() => {
    if (isMobile) return;

    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        if (isOpen) {
          setIsClosing(true);
          setTimeout(() => {
            setIsOpen(false);
            setIsClosing(false);
          }, 300);
        }
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, isMobile]);

  // fecha o dropdown quando o menu mobile é fechado
  useEffect(() => {
    if (isMobile && !isMobileMenuOpen && isOpen) {
      setIsClosing(true);
      setTimeout(() => {
        setIsOpen(false);
        setIsClosing(false);
      }, 300);
    }
  }, [isMobileMenuOpen, isMobile, isOpen]);

  // versão mobile para menu sidebar - lista de idiomas
  if (isMobile) {
    return (
      <div className={styles.languageSelectorMobile} ref={wrapperRef}>
        <div className={styles.mobileLanguageHeader}>
          <FaGlobe className={styles.globeIconMobile} />
          <span className={styles.mobileLanguageTitle}>
            <FormattedMessage id="header.language" defaultMessage="Idioma" />
          </span>
        </div>

        <button
          className={`${styles.mobileLanguageButton} ${
            isOpen ? styles.open : ""
          }`}
          onClick={() => {
            if (isOpen) {
              setIsClosing(true);
              setTimeout(() => {
                setIsOpen(false);
                setIsClosing(false);
              }, 300);
            } else {
              setIsOpen(true);
            }
          }}
          aria-label="Selecionar idioma"
        >
          <img
            src={selectedLanguage.flag}
            alt={selectedLanguage.name}
            width={20}
            height={14}
            style={{ marginRight: "8px" }}
          />
          <span className={styles.mobileLanguageText}>
            {selectedLanguage.name}
          </span>
          <FaChevronDown
            className={`${styles.chevronIconMobile} ${
              isOpen ? styles.open : ""
            }`}
          />
        </button>

        {(isOpen || isClosing) && (
          <div
            className={`${styles.dropdownMenuMobile} ${
              isOpen && !isClosing ? styles.open : styles.closing
            }`}
          >
            {languages.map(lang => (
              <button
                key={lang.code}
                className={styles.dropdownMenuItem}
                onClick={() => handleLanguageChange(lang.code)}
              >
                <img
                  src={lang.flag}
                  alt={lang.name}
                  width={20}
                  height={14}
                  style={{ marginRight: "8px" }}
                />
                {lang.name}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // versão desktop - apenas bandeira, expande ao clicar
  return (
    <div className={styles.languageSelector} ref={wrapperRef}>
      <button
        type="button"
        className={`${styles.selectorButton} ${styles.selectorButtonFlagOnly} ${isOpen ? styles.open : ""}`}
        onClick={() => {
          if (isOpen) {
            setIsClosing(true);
            setTimeout(() => {
              setIsOpen(false);
              setIsClosing(false);
            }, 300);
          } else {
            setIsOpen(true);
          }
        }}
        aria-label={selectedLanguage.name}
        title={selectedLanguage.name}
      >
        <img
          src={selectedLanguage.flag}
          alt=""
          width={FLAG_SIZE}
          height={FLAG_HEIGHT}
          className={styles.flagIcon}
        />
      </button>

      {(isOpen || isClosing) && (
        <ul
          className={`${styles.dropdownMenu} ${styles.dropdownMenuFlags} ${
            isOpen && !isClosing ? styles.open : styles.closing
          }`}
        >
          {languages.map(lang => (
            <li
              key={lang.code}
              className={locale === lang.code ? styles.flagItemActive : ""}
              onClick={() => handleLanguageChange(lang.code)}
            >
              <img
                src={lang.flag}
                alt={lang.name}
                width={FLAG_SIZE}
                height={FLAG_HEIGHT}
                title={lang.name}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LanguageSelector;
